// Shared inbox service for threads + unread counts
// Single source of truth for messaging state across the app
import { db } from "./firebase";
import { collection, doc, query, orderBy, limit, onSnapshot, setDoc, serverTimestamp, getDocs, getDocsFromServer } from "firebase/firestore";
import type { Timestamp, SnapshotMetadata } from "firebase/firestore";

export interface UserThreadDoc {
  threadId: string;
  otherUid: string;
  otherName?: string;
  lastMessageAt: Timestamp;
  lastMessage?: string;
  createdAt: Timestamp;
  lastReadAt?: Timestamp; // When user last viewed this thread
  hasUnread?: boolean; // True if thread has unread messages
}

/**
 * Subscribe to user's threads with realtime updates
 * Returns unsubscribe function
 */
const DEFAULT_THREAD_LIST_LIMIT = 80;

function mapUserThreadSnapshotDocs(
  docs: Array<{ id: string; data: () => Record<string, unknown> }>
): UserThreadDoc[] {
  const threads = docs.map((d) => {
    const data = d.data();
    return {
      id: d.id,
      threadId: (data.threadId as string) || d.id,
      otherUid: (data.otherUid as string) || "",
      otherName: data.otherName as string | undefined,
      lastMessageAt: data.lastMessageAt as Timestamp,
      lastMessage: data.lastMessage as string | undefined,
      createdAt: data.createdAt as Timestamp,
      lastReadAt: data.lastReadAt as Timestamp | undefined,
      hasUnread: data.hasUnread as boolean | undefined,
    } as UserThreadDoc;
  });
  threads.sort((a, b) => {
    const aTime = a.lastMessageAt?.toMillis?.() || 0;
    const bTime = b.lastMessageAt?.toMillis?.() || 0;
    return bTime - aTime;
  });
  return threads;
}

/**
 * Inbox thread list: seed from server first, then realtime updates.
 * Ignores stale IndexedDB empty cache snapshots (scoped to userThreads only).
 */
export function listenMyThreads(
  uid: string,
  onChange: (threads: UserThreadDoc[], meta: SnapshotMetadata) => void,
  options?: {
    maxThreads?: number;
    /** Called when the listener fails. When set, a failed listener no longer reports an empty list. */
    onError?: (err: unknown) => void;
  }
): () => void {
  if (!db) {
    onChange([], { fromCache: false, hasPendingWrites: false } as SnapshotMetadata);
    return () => {};
  }
  const maxThreads = Math.min(Math.max(options?.maxThreads ?? DEFAULT_THREAD_LIST_LIMIT, 1), 200);
  const threadsRef = collection(db, "userThreads", uid, "threads");
  const q = query(threadsRef, limit(maxThreads));

  let cancelled = false;
  let sawServerSnapshot = false;
  let unsubscribeSnapshot: (() => void) | undefined;

  void (async () => {
    try {
      const serverSnap = await getDocsFromServer(q);
      if (cancelled) return;
      sawServerSnapshot = true;
      onChange(mapUserThreadSnapshotDocs(serverSnap.docs), serverSnap.metadata);
    } catch (err) {
      if (import.meta.env.DEV) {
        console.warn("[INBOX_REPO] Server thread seed failed, waiting for listener:", err);
      }
    }

    if (cancelled) return;

    unsubscribeSnapshot = onSnapshot(
      q,
      (snapshot) => {
        if (snapshot.metadata.fromCache && snapshot.empty) {
          return;
        }
        if (snapshot.metadata.fromCache && !sawServerSnapshot) {
          return;
        }
        if (!snapshot.metadata.fromCache) {
          sawServerSnapshot = true;
        }
        onChange(mapUserThreadSnapshotDocs(snapshot.docs), snapshot.metadata);
      },
      (err) => {
        console.error("[INBOX_REPO] Thread listener error:", err);
        if (options?.onError) {
          options.onError(err);
        } else if (!sawServerSnapshot) {
          onChange([], { fromCache: false, hasPendingWrites: false } as SnapshotMetadata);
        }
      }
    );
  })();

  return () => {
    cancelled = true;
    unsubscribeSnapshot?.();
  };
}

/**
 * Unread logic shared by badge snapshot + optional one-shot count queries.
 * Align caps with {@link listenMyThreads} so badges match recent-thread UX.
 */
/** Match {@link DEFAULT_THREAD_LIST_LIMIT} so one-shot count aligns with listener footprint. */
const BADGE_THREAD_SCAN_LIMIT = 80;

export function countUnreadFromThreadDocs(
  threads: UserThreadDoc[],
  opts?: { excludeThreadId?: string | null }
): number {
  const skipId = opts?.excludeThreadId?.trim() || null;
  let unreadCount = 0;
  for (const data of threads) {
    const tid = data.threadId || (data as UserThreadDoc & { id?: string }).id;
    if (skipId && tid === skipId) continue;

    if (data.hasUnread === false) {
      continue;
    }
    if (data.hasUnread === true) {
      unreadCount++;
      continue;
    }
    if (data.lastMessageAt && data.lastReadAt) {
      const lastMsgTime = data.lastMessageAt.toMillis?.() || 0;
      const lastReadTime = data.lastReadAt.toMillis?.() || 0;
      if (lastMsgTime > lastReadTime) unreadCount++;
    } else if (data.lastMessageAt && !data.lastReadAt) {
      unreadCount++;
    }
  }
  return unreadCount;
}

/**
 * One-shot unread estimate (recent threads only).
 */
export async function getUnreadThreadsCount(
  uid: string,
  opts?: { excludeThreadId?: string | null }
): Promise<number> {
  try {
    if (!db) return 0;
    const threadsRef = collection(db, "userThreads", uid, "threads");
    let snap;
    try {
      snap = await getDocsFromServer(query(threadsRef, limit(BADGE_THREAD_SCAN_LIMIT)));
    } catch {
      snap = await getDocs(query(threadsRef, limit(BADGE_THREAD_SCAN_LIMIT)));
    }
    const threads = snap.docs.map((doc) => {
      const data = doc.data();
      return {
        threadId: data.threadId || doc.id,
        otherUid: data.otherUid || '',
        lastMessageAt: data.lastMessageAt,
        lastReadAt: data.lastReadAt,
        hasUnread: data.hasUnread,
      } as UserThreadDoc;
    });
    return countUnreadFromThreadDocs(threads, { excludeThreadId: opts?.excludeThreadId });
  } catch (err: any) {
    if (import.meta.env.DEV) console.error("[INBOX_REPO] Failed to get unread count:", err);
    return 0;
  }
}

/**
 * Mark thread as read: update userThreads (for list/badge) and conversation doc (persisted across logins).
 */
export async function markThreadRead(uid: string, threadId: string): Promise<void> {
  if (!db) return;
  try {
    const userThreadRef = doc(db, "userThreads", uid, "threads", threadId);
    await setDoc(userThreadRef, { lastReadAt: serverTimestamp(), hasUnread: false }, { merge: true });
  } catch (err: any) {
    if (import.meta.env.DEV) console.error("[INBOX_REPO] Failed to mark userThread read:", err);
  }
  try {
    const convoRef = doc(db, "conversations", threadId);
    await setDoc(convoRef, { [`lastReadAtByUid.${uid}`]: serverTimestamp() }, { merge: true });
  } catch (err: any) {
    if (import.meta.env.DEV) console.error("[INBOX_REPO] Failed to mark conversation read:", err);
  }
}
